import { type FormHTMLAttributes, lazy, type ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteLoaderData } from 'react-router';

import type { RedemptionModalOrderData } from '@shared/dream-components/src';
import { useQueryClient } from '@tanstack/react-query';

import ActionForm from '~/components/ActionForm';
import { useComponentFetcher } from '~/components/ComponentErrorBoundary/useComponentFetcher';
import { usePageContext } from '~/context/usePageContext';
import useBoostAffiliate, { BOOST_AFFILIATE_COOKIE_NAME } from '~/hooks/useBoostAffiliate';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import { webinarDetailsKeys } from '~/hooks/useLoaders/useWebinarDetails';
import type { ToastData } from '~/hooks/useToast';
import type { CalComBooking } from '~/interfaces/cal_com';
import type { FormResponse } from '~/interfaces/form';
import type { Product } from '~/interfaces/product';
import type { RootLoaderData } from '~/root';
import { BookWebinarModal } from '~/routes/products/_components/BookWebinarModal';
import { useClientSecretContext } from '~/routes/products/_hooks/useClientSecretContext';
import { appendFormResponses } from '~/utils/extractFormResponses';
import getProductVariantCoachingSessionDetails from '~/utils/getProductVariantCoachingSessionDetails';
import getProductVariantWebinarDetails, { isWebinarStatusPurchasable } from '~/utils/getProductVariantWebinarDetails';
import { getNectarVisitToken, getOrCreateAnonymousUserId } from '~/utils/nectarTracking';
import { NECTAR_SITE_ID_FIELD } from '~/utils/nectarTrackingFields';
import toast from '~/utils/toast';
import { getCheckoutClientSecret, saveCheckoutClientSecret } from '~/utils/webinarPendingCheckout';

interface BuyNowActionProps {
  product: Product;
  reservationRedirectUrl?: string | null;
  children: ({ isLoading }: { isLoading: boolean }) => ReactNode | ReactNode[];
  formProps?: FormHTMLAttributes<HTMLFormElement>;
}

// Loaded lazily so @calcom/atoms (the heaviest dependency in the reader
// bundle) only downloads on cal.com coaching product pages, not on every
// published page that renders a Button node.
const CoachingBookingModal = lazy(() => import('./CoachingBookingModal'));

const BuyNowAction = ({ product, reservationRedirectUrl, children, formProps }: BuyNowActionProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { subscription, site } = useRouteLoaderData('root') as RootLoaderData;
  const { openPageModalType, setOpenPageModalType, visitToken, referralId, cookies } = usePageContext();
  const boostAffiliateRecommendingPublicationId = useBoostAffiliate(cookies[BOOST_AFFILIATE_COOKIE_NAME] || undefined);
  const fetcher = useComponentFetcher();
  const formRef = useRef<HTMLFormElement>(null);
  const {
    setClientSecret,
    setRedemptionToken,
    setShowThankYou,
    setShowFreeProductCheckoutModal,
    shouldInstantlyRedeem,
    setOrderStatusData,
    setRedemptionState,
    setRedemptionErrorMessage,
    setShowRedemptionModal,
  } = useClientSecretContext();
  const isLoggedIn = useIsLoggedIn();

  const productVariantId = product.default_product_variant?.id ?? '';

  const handleBookingAlreadyExists = () => {
    if (!productVariantId) return;
    const storedSecret = getCheckoutClientSecret(productVariantId);
    if (storedSecret) {
      setOpenPageModalType(null);
      setClientSecret(storedSecret);
    } else {
      toast.error(t('products.error.booking_conflict'));
    }
  };

  const isProductArchived = product.status === 'archived';
  const isProductSoldOut = product.sold_out;
  const webinarRedeemable = product.default_product_variant?.redeemable;
  const isWebinarUnavailable =
    webinarRedeemable?.type === 'ProductRedeemables::Webinar' && !isWebinarStatusPurchasable(webinarRedeemable.status);

  const { isCoachingSession, coachingSessionProvider } = getProductVariantCoachingSessionDetails(product);
  const { hasCalComScheduling } = getProductVariantWebinarDetails(product);

  const calComRedeemableMetadata: {
    id: number;
    slug: string;
    users: {
      username: string;
    }[];
  } = product.default_product_variant?.redeemable?.metadata;

  const hasCalComBooking = (isCoachingSession && coachingSessionProvider === 'cal_com') || hasCalComScheduling;

  const isCalComCoachingProduct =
    isCoachingSession &&
    coachingSessionProvider === 'cal_com' &&
    Boolean(calComRedeemableMetadata?.slug) &&
    Boolean(calComRedeemableMetadata?.users?.[0]?.username);

  // Mounted on first open and kept mounted after; the chunk is warmed while
  // the browser is idle so the first click doesn't wait on the network.
  const [hasOpenedBookingModal, setHasOpenedBookingModal] = useState(false);

  useEffect(() => {
    if (isCalComCoachingProduct && openPageModalType === 'booking_modal') {
      setHasOpenedBookingModal(true);
    }
  }, [isCalComCoachingProduct, openPageModalType]);

  useEffect(() => {
    if (!isCalComCoachingProduct) return;

    const warmUp = () => void import('./CoachingBookingModal');
    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(warmUp);
      return () => window.cancelIdleCallback(idleId);
    }
    const timeoutId = window.setTimeout(warmUp, 2000);
    return () => window.clearTimeout(timeoutId);
  }, [isCalComCoachingProduct]);

  const isFreeProduct = product.free;
  const hasRequiredForm =
    Boolean(product.default_product_variant?.required_form_id) &&
    product.default_product_variant?.required_form !== null;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Backstop in case the button hasn't disabled yet (race during initial query).
    if (reservationRedirectUrl) {
      e.preventDefault();
      window.location.href = reservationRedirectUrl;
      return false;
    }

    if (isWebinarUnavailable) {
      e.preventDefault();
      toast.error(t('products.registration_closed_toast_message'));
      return false;
    }
    if (isProductSoldOut) {
      e.preventDefault();
      toast.error(t('products.sold_out_toast_message'));
      return false;
    }
    if (isProductArchived) {
      e.preventDefault();
      toast.error(t('products.archived_toast_message'));
      return false;
    }

    if (hasCalComBooking) {
      e.preventDefault();

      if (!calComRedeemableMetadata?.slug || !calComRedeemableMetadata?.users?.[0]?.username) {
        toast.error('Something went wrong, please try again');
        return false;
      }

      // Free webinar + guest: route through signup flow for bot detection and
      // consistent behaviour with other free products (link-based, file-based)
      if (isFreeProduct && !isLoggedIn) {
        setShowFreeProductCheckoutModal(true);
        return false;
      }

      setOpenPageModalType('booking_modal');
      return false;
    }

    // For free products, show free product checkout modal if:
    // - User is not logged in, OR
    // - User is logged in and Product has a required form (form must be filled even for logged-in users)
    if (isFreeProduct && ((isLoggedIn && hasRequiredForm) || !isLoggedIn)) {
      e.preventDefault();
      setShowFreeProductCheckoutModal(true);
      return false;
    }

    if (!formRef.current) {
      return;
    }

    const formData = new FormData(formRef.current);
    if (typeof window !== 'undefined') {
      formData.set('purchase_url', window.location.href);
      formData.set('anonymous_user_id', getOrCreateAnonymousUserId());
      formData.set('nectar_visit_token', getNectarVisitToken());
      if (site.id) formData.set(NECTAR_SITE_ID_FIELD, site.id);
    }
    fetcher.submit(formData, {
      method: 'post',
      action: `/products/${product.slug}`,
    });
  };

  const handleBookingSuccess = (
    bookingData: CalComBooking,
    webinarAccessToken?: string,
    formResponses: FormResponse[] = [],
  ) => {
    if (!formRef.current) {
      return;
    }

    const formData = new FormData(formRef.current);
    formData.append('booking_data', JSON.stringify(bookingData));
    formData.append('booking_provider', 'cal_com');
    if (webinarAccessToken) {
      formData.append('webinar_access_token', webinarAccessToken);
    }
    appendFormResponses(formData, formResponses);
    formData.append('booking_attendee_email', bookingData.attendees[0]?.email ?? '');
    formData.append('booking_attendee_name', bookingData.attendees[0]?.name ?? '');

    fetcher.submit(formData, {
      method: 'post',
      action: `/products/${product.slug}`,
    });

    queryClient.invalidateQueries({ queryKey: webinarDetailsKeys.bySlug(product.slug) });
  };

  const handleCoachingBookingSuccess = (bookingData: CalComBooking) => {
    // Access token is only required for webinars; coaching sessions don't create `WebinarReservation`.
    handleBookingSuccess(bookingData);
  };

  return (
    <>
      {hasCalComScheduling && calComRedeemableMetadata?.id && (
        <BookWebinarModal
          isOpen={openPageModalType === 'booking_modal'}
          onClose={() => setOpenPageModalType(null)}
          onBookingSuccess={handleBookingSuccess}
          shouldDisableConfirmButton={fetcher.state === 'submitting'}
          productSlug={product.slug}
          eventName={product.name}
          eventStartsAt={webinarRedeemable?.event_starts_at ?? ''}
          eventEndsAt={webinarRedeemable?.event_ends_at}
          eventTimeZone={webinarRedeemable?.iana_time_zone ?? webinarRedeemable?.time_zone ?? 'UTC'}
          remainingSeats={webinarRedeemable?.remaining_seats}
          maxSeats={webinarRedeemable?.max_seats}
          currentSubscriberEmail={subscription?.email}
          onBookingAlreadyExists={handleBookingAlreadyExists}
          form={isFreeProduct ? (product.default_product_variant?.required_form ?? undefined) : undefined}
        />
      )}
      {isCalComCoachingProduct && hasOpenedBookingModal && (
        <Suspense fallback={null}>
          <CoachingBookingModal
            onBookingSuccess={handleCoachingBookingSuccess}
            shouldDisableConfirmButton={fetcher.state === 'submitting'}
            eventSlug={calComRedeemableMetadata.slug}
            calComUserName={calComRedeemableMetadata.users[0].username}
            currentSubscriberEmail={subscription?.email}
            onClose={() => setOpenPageModalType(null)}
            isOpen={openPageModalType === 'booking_modal'}
          />
        </Suspense>
      )}
      <ActionForm
        formProps={formProps}
        fetcher={fetcher}
        formRef={formRef}
        method="post"
        action={`/products/${product.slug}`}
        onSuccess={(data: {
          order?: RedemptionModalOrderData;
          clientSecret?: string;
          toast?: ToastData;
          redemptionToken?: string;
          already_registered?: boolean;
          redirect_url?: string;
        }) => {
          if (data?.already_registered && data.redirect_url) {
            toast.success(t('products.webinar.already_registered'));
            window.location.href = data.redirect_url;
            return;
          }

          if (data?.redemptionToken) {
            setRedemptionToken(data.redemptionToken);
          }

          setShowFreeProductCheckoutModal(false);
          setOpenPageModalType(null);

          // Paid product: show Stripe checkout modal
          if (data.clientSecret && setClientSecret) {
            if (productVariantId) {
              saveCheckoutClientSecret(productVariantId, data.clientSecret);
            }
            setClientSecret(data.clientSecret);
            return;
          }

          // No order in response: nothing left to show
          if (!data.order?.id) {
            return;
          }

          // Free product with instant redemption: show redemption modal (download/access link)
          if (shouldInstantlyRedeem && data.redemptionToken) {
            setOrderStatusData(data.order);
            setRedemptionState('ready');
            setRedemptionErrorMessage(null);
            setShowRedemptionModal(true);
            return;
          }

          // Free product without instant redemption: show thank you message
          setShowThankYou(true);
        }}
        onSubmit={handleSubmit}
        onError={(data) => {
          toast.error(data?.toast?.message || 'Something went wrong, please try again');
        }}
      >
        <input type="hidden" name="product_variant_id" value={product.default_product_variant?.id || ''} />
        <input hidden name="sent_from_orchid" value="true" />
        {referralId && <input hidden name="ref" value={referralId} onChange={() => null} />}
        {boostAffiliateRecommendingPublicationId && (
          <input hidden name="bhba" value={boostAffiliateRecommendingPublicationId} onChange={() => null} />
        )}
        {visitToken && <input hidden name="visit_token" value={visitToken} onChange={() => null} />}
        <input hidden name="is_js_enabled" defaultValue="true" />
        {children({ isLoading: fetcher.state === 'submitting' })}
      </ActionForm>
    </>
  );
};

export default BuyNowAction;
